import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  HostListener,
  input,
  output,
  ElementRef,
  ViewChild,
  inject,
} from '@angular/core';
import { SideMenuState } from 'src/app/store/side-menu/side-menu.state';
import { DeviceState } from 'src/app/store/device/device.state';

@Component({
  selector: 'app-left-menu-panel',
  templateUrl: './left-menu-panel.component.html',
  styleUrls: ['./left-menu-panel.component.scss'],
  host: {
    class: 'contents',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
  ],
})
export class LeftMenuPanelComponent implements OnDestroy {
  // --- Injected dependencies ---
  private readonly sideMenuState = inject(SideMenuState);
  private readonly deviceState = inject(DeviceState);

  @ViewChild('sideMenuContainer') sideMenuContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('sideMenuContent') sideMenuContent!: ElementRef<HTMLDivElement>;
  /**
   * General expanded state input (for non-workspace usage)
   */
  expanded = input<boolean>(false);

  /**
   * Swipe state variables for swipe-to-dismiss gesture
   */
  private isSwiping = false;
  isDismissingViaSwipe = false; // Public for potential template access
  private startX = 0;
  private startY = 0;
  private currentX = 0;
  private startTime = 0;
  private swipeDirection: 'horizontal' | 'vertical' | null = null;

  /**
   * Swipe thresholds
   */
  private readonly SWIPE_DISTANCE_THRESHOLD = 50; // pixels to swipe for dismiss
  private readonly SWIPE_VELOCITY_THRESHOLD = 0.3; // pixels per ms for quick swipe
  private readonly DIRECTION_THRESHOLD = 10; // pixels to move before determining direction

  onDragDismiss = output<void>();

  /**
   * Listen for global mouse move (for swipe)
   */
  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isSwiping) {
      this.onSwipeMove(event);
    }
  }

  /**
   * Listen for global mouse up (for swipe)
   */
  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (this.isSwiping) {
      this.onSwipeEnd(event);
    }
  }

  /**
   * Listen for global touch move (for swipe)
   */
  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (this.isSwiping) {
      this.onSwipeMove(event);
    }
  }

  /**
   * Listen for global touch end (for swipe)
   */
  @HostListener('document:touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (this.isSwiping) {
      this.onSwipeEnd(event);
    }
  }

  toggleSideMenu(): void {
    this.sideMenuState.toggleSideNavExpanded();
  }

  ngOnDestroy() { }

  /**
   * Handle touch/mouse start for swipe gesture
   */
  onSwipeStart(event: TouchEvent | MouseEvent): void {
    // Only enable swipe on mobile when side menu is expanded
    if (!this.deviceState.isSmallerThanLargeScreen() || !this.expanded()) return;

    this.isSwiping = true;
    this.swipeDirection = null; // Reset direction on new swipe
    this.startTime = Date.now();

    if (event instanceof TouchEvent) {
      this.startX = event.touches[0].clientX;
      this.startY = event.touches[0].clientY;
    } else {
      this.startX = event.clientX;
      this.startY = event.clientY;
    }

    this.currentX = this.startX;
  }

  /**
   * Handle touch/mouse move for swipe gesture
   */
  onSwipeMove(event: TouchEvent | MouseEvent): void {
    if (!this.isSwiping) return;

    if (!this.deviceState.isSmallerThanLargeScreen()) return;

    let clientX: number;
    let clientY: number;
    if (event instanceof TouchEvent) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }

    const deltaX = clientX - this.startX;
    const deltaY = clientY - this.startY;

    // Determine swipe direction if not yet determined
    if (this.swipeDirection === null) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      // Only determine direction once we've moved enough
      if (absX > this.DIRECTION_THRESHOLD || absY > this.DIRECTION_THRESHOLD) {
        this.swipeDirection = absX > absY ? 'horizontal' : 'vertical';
      }
    }

    // If swiping vertically, allow default scroll behavior
    if (this.swipeDirection === 'vertical') {
      return;
    }

    // Only prevent default for horizontal swipes to avoid interfering with vertical scroll
    if (this.swipeDirection === 'horizontal' && event instanceof TouchEvent) {
      event.preventDefault();

      // Disable y-scrolling during horizontal swipe
      this.disableVerticalScroll();
    }

    // Track horizontal movement for swipe detection
    if (this.swipeDirection === 'horizontal') {
      this.currentX = clientX;
    }
  }

  /**
   * Handle touch/mouse end for swipe gesture
   */
  onSwipeEnd(event: TouchEvent | MouseEvent): void {
    if (!this.isSwiping) return;

    if (!this.deviceState.isSmallerThanLargeScreen()) return;

    // If this was a vertical swipe, just reset and return
    if (this.swipeDirection === 'vertical') {
      this.isSwiping = false;
      this.swipeDirection = null;
      // Ensure vertical scroll is enabled (in case it was disabled)
      this.enableVerticalScroll();
      return;
    }

    const deltaX = this.currentX - this.startX;
    const velocity = this.calculateVelocity(deltaX);

    const sideMenuElement = this.getSideMenuElement();

    // Determine if we should dismiss based on swipe direction and velocity
    // Left swipe (deltaX < 0) with sufficient distance or velocity
    const shouldDismiss =
      (deltaX < 0 && Math.abs(deltaX) > this.SWIPE_DISTANCE_THRESHOLD) ||
      velocity > this.SWIPE_VELOCITY_THRESHOLD;

    if (shouldDismiss) {
      // Set flag to bypass any exit animations if needed
      this.isDismissingViaSwipe = true;

      // Dismiss the side menu with animation
      if (sideMenuElement) {
        sideMenuElement.style.transition = 'transform 200ms ease-out';
        sideMenuElement.style.transform = 'translateX(-100%)';
      }

      // Close after animation completes
      setTimeout(() => {
        this.onDragDismiss.emit();
        this.resetSwipeState();
        // Reset flag after a delay
        setTimeout(() => {
          this.isDismissingViaSwipe = false;
        }, 50);
      }, 200);
    } else {
      // No visual snap back needed since there was no visual feedback during swipe
      this.resetSwipeState();
    }

    this.isSwiping = false;
    this.swipeDirection = null;
  }

  /**
   * Calculate swipe velocity for quick swipe detection
   * @param deltaX - The horizontal distance swiped
   */
  private calculateVelocity(deltaX: number): number {
    const deltaTime = Date.now() - this.startTime;
    if (deltaTime === 0) return 0;

    // Calculate velocity in pixels per millisecond
    return Math.abs(deltaX) / deltaTime;
  }

  /**
   * Get the side menu element
   */
  private getSideMenuElement(): HTMLElement | null {
    return this.sideMenuContainer?.nativeElement;
  }

  private getSideMenuContentElement(): HTMLElement | null {
    return this.sideMenuContent?.nativeElement;
  }

  /**
   * Disable vertical scrolling on the menu container and content
   */
  private disableVerticalScroll(): void {
    const sideMenuElement = this.getSideMenuElement();
    const contentElement = this.getSideMenuContentElement();

    if (sideMenuElement) {
      sideMenuElement.style.overflowY = 'hidden';
    }
    if (contentElement) {
      contentElement.style.overflowY = 'hidden';
    }
  }

  /**
   * Re-enable vertical scrolling on the menu container and content
   */
  private enableVerticalScroll(): void {
    const sideMenuElement = this.getSideMenuElement();
    const contentElement = this.getSideMenuContentElement();

    if (sideMenuElement) {
      sideMenuElement.style.overflowY = '';
    }
    if (contentElement) {
      contentElement.style.overflowY = '';
    }
  }

  /**
   * Reset swipe state and clear inline styles
   */
  private resetSwipeState(): void {
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.startTime = 0;
    this.swipeDirection = null;

    const sideMenuElement = this.getSideMenuElement();
    if (sideMenuElement) {
      sideMenuElement.style.transition = '';
      sideMenuElement.style.transform = '';
    }

    // Re-enable vertical scrolling
    this.enableVerticalScroll();
  }
}
